import { ReactNode } from 'react';
import Box from '@material-ui/core/Box';
import Chip from '@material-ui/core/Chip';
import Divider from '@material-ui/core/Divider';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import { makeStyles } from '@material-ui/core/styles';
import { Link } from '@backstage/core-components';
import { createPlugin } from '@backstage/core-plugin-api';
import {
  createSearchResultListItemExtension,
  HighlightedSearchResultText,
  SearchResultListItemExtensionProps,
} from '@backstage/plugin-search-react';
import { ResultHighlight, SearchDocument } from '@backstage/plugin-search-common';

/**
 * Mirrors the document shape emitted by search-backend-module-openapi.
 */
type OpenApiEndpointDocument = SearchDocument & {
  apiName?: string;
  documentKind?: 'endpoint' | 'spec';
  method?: string;
  path?: string;
  operationId?: string;
};

const useStyles = makeStyles(theme => ({
  item: {
    display: 'flex',
  },
  method: {
    fontFamily: 'monospace',
    fontWeight: 600,
    height: theme.spacing(2.5),
  },
  path: {
    fontFamily: 'monospace',
    wordBreak: 'break-all',
  },
  excerpt: {
    display: '-webkit-box',
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: 2,
    overflow: 'hidden',
  },
}));

/**
 * Colour by side effect rather than by verb, so a DELETE reads as destructive
 * at a glance without inventing a palette entry per method.
 */
const methodColor = (method?: string) => {
  switch (method) {
    case 'GET':
    case 'HEAD':
    case 'OPTIONS':
      return 'default' as const;
    case 'DELETE':
      return 'secondary' as const;
    default:
      return 'primary' as const;
  }
};

export type OpenApiSearchResultListItemProps =
  SearchResultListItemExtensionProps<{
    icon?: ReactNode;
    result?: SearchDocument;
    highlight?: ResultHighlight;
  }>;

export const OpenApiSearchResultListItem = (
  props: OpenApiSearchResultListItemProps,
) => {
  const { icon, highlight } = props;
  const classes = useStyles();
  const result = props.result as OpenApiEndpointDocument | undefined;

  if (!result) {
    return null;
  }

  const isEndpoint = result.documentKind === 'endpoint' && result.path;

  return (
    <>
      <ListItem alignItems="flex-start" className={classes.item}>
        {icon && <ListItemIcon>{icon}</ListItemIcon>}
        <ListItemText
          primaryTypographyProps={{ variant: 'body1', component: 'div' }}
          primary={
            <Link noTrack to={result.location}>
              {isEndpoint ? (
                <Box display="flex" alignItems="center" gridGap={8}>
                  <Chip
                    className={classes.method}
                    label={result.method}
                    size="small"
                    color={methodColor(result.method)}
                  />
                  <span className={classes.path}>{result.path}</span>
                </Box>
              ) : (
                result.title
              )}
            </Link>
          }
          secondaryTypographyProps={{ variant: 'caption', component: 'div' }}
          secondary={
            <>
              {result.apiName && <Box component="span">{result.apiName}</Box>}
              {result.operationId && (
                <Box component="span"> · {result.operationId}</Box>
              )}
              <Box className={classes.excerpt}>
                {highlight?.fields.text ? (
                  <HighlightedSearchResultText
                    text={highlight.fields.text}
                    preTag={highlight.preTag}
                    postTag={highlight.postTag}
                  />
                ) : (
                  result.text
                )}
              </Box>
            </>
          }
        />
      </ListItem>
      <Divider component="li" />
    </>
  );
};

// createSearchResultListItemExtension needs a plugin to hang the extension on,
// and only extensions carrying its component data are considered by
// <SearchResult>; a plain component child would be silently ignored.
const openApiSearchPlugin = createPlugin({ id: 'openapi-search' });

export const OpenApiSearchResultListItemExtension = openApiSearchPlugin.provide(
  createSearchResultListItemExtension({
    name: 'OpenApiSearchResultListItem',
    component: async () => OpenApiSearchResultListItem,
    predicate: result => result.type === 'openapi-endpoint',
  }),
);
